import Link from 'ageas-react-component-link'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import Action from 'ageas-react-component-action'
import Date from 'ageas-react-component-datepicker'
import PolicyData from 'ageas-react-component-greybackground'
import MiniLoader from 'ageas-react-component-miniloader'
import moment from 'moment'

import { AnimalInsuranceTable } from '../../Components/AnimalInsuranceTable'
import { LegalInformation } from '../../Components/LegalInformation'
import { UserInfo } from '../../Components/UserInfo'
import { MainContainer } from '../../GlobalStyles'
import { LocalStorageService } from '../../Services/LocalStorage'
import { useAnimalStore } from '../../Stores/animalStore'
import { useContextStore } from '../../Stores/contextStore'
import { usePolicyholderDetailsStore } from '../../Stores/policyholderDetailsStore'
import { useProgressStore } from '../../Stores/progressStore'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useAgent } from '../../Stores/useAgent'
import { useAnimalValidateStore } from '../../Stores/useAnimalValidateStore'
import { useCreateProposalStore } from '../../Stores/useCreateProposalStore'
import { useFillDataForSimulationStore } from '../../Stores/useFillDataForSimulationStore'
import { useGenerateProposalDocumentsStore } from '../../Stores/useGenerateProposalDocumentsStore'
import { useGetEntitiesDetailsStore } from '../../Stores/useGetEntitiesDetailsStore'
import { useGetQuotationStore } from '../../Stores/useGetQuotationStore'
import { useRequestQuotationStore } from '../../Stores/useRequestQuotationStore'
import { useResumeStore } from '../../Stores/useResumeStore'
import { useRunPetisSimulationStore } from '../../Stores/useRunPetisSimulationStore'
import { useWarningStore } from '../../Stores/warningStore'
import { LOCAL_STORAGE } from '../../constants/localStorage'
import { ROUTES } from '../../constants/routes'
import { WARNING_BAR } from '../../constants/warningBar'
import { useResumePetisSimulationProposal } from '../../hooks/api/resumePetisSimulationProposal/useResumePetisSimulationProposal'
import { useResumePetisSimulationProposalPayload } from '../../hooks/api/resumePetisSimulationProposal/useResumePetisSimulationProposalPayload'
import { useFillDataForSimulation } from '../../hooks/api/useFillDataForSimulation'
import { useLoadPolicyHolderReferenceData } from '../../hooks/api/useLoadPolicyHolderReferenceData'
import { useURL } from '../../hooks/hydration/useURL'
import { useNewSimulation } from '../../hooks/useNewSimulation'
import { t } from '../../i18n'
import { Entity } from './Entity'
import { PolicyDataElements, SimulateDataContainer } from './style'

const TODAY = moment().format('DD-MM-YYYY')
const THIRTY_DAYS_FROM_TODAY = moment(TODAY, 'DD-MM-YYYY')
  .add(30, 'days')
  .format('DD-MM-YYYY')

export const DataToSimulate = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { referenceNumber, handleUpdateURLWithoutReload } = useURL()
  const [requestId, setRequestId] = useState('')
  const [isFiscalNumberFilled, setIsFiscalNumberFilled] = useState(false)
  const [isFiscalNumberSearched, setIsFiscalNumberSearched] = useState(false)
  const [validateInitialDate, setValidateInitialDate] = useState(false)
  const [isEntityDetailsLoading, setIsEntityDetailsLoading] = useState(false)
  const [isStorageClear, setIsStorageClear] = useState(false)
  const [messageDate, setMessageDate] = useState('')
  const { agentIdBySessionStorage } = useAgent()
  const { handleNewSimulation, handleClearLocalStorage } = useNewSimulation()
  const { isValid, setIsValid } = useAnimalValidateStore()
  const { setWarningOptions } = useWarningStore()
  const { globalContext } = useContextStore()
  const { animalsList, updateAnimal } = useAnimalStore()
  const { fillDataForSimulationRequest } = useFillDataForSimulationStore()
  const { setIsResumeLoading, isResume } = useResumeStore()
  const { setCurrentStep } = useProgressStore()
  const { isGenerateProposalDocuments } = useGenerateProposalDocumentsStore()
  const { getEntitiesDetails } = useGetEntitiesDetailsStore()
  const { policyholderDetailsSelected } = usePolicyholderDetailsStore()
  const { disabledStepsByResume } = useResumeStore()
  const { resumePetisSimulationProposalPayload } =
    useResumePetisSimulationProposalPayload(referenceNumber || '')
  const { hasCreatedProposal } = useCreateProposalStore()
  const { requestQuotation, resetRequestQuotation } = useRequestQuotationStore()
  const { resetGetQuotation } = useGetQuotationStore()
  const {
    setIsReferral,
    setHasRunPetisUpdated,
    setEnabledRunPetisRequest,
    enabledRunPetisRequest,
    runPetisSimulation,
    setSelectedPackCampaign,
  } = useRunPetisSimulationStore()

  const {
    initialDate,
    setInitialDate,
    entitySelected,
    resetTotalPricesObjects,
    setIsFullStop,
    setIsRecalculate,
    setShowSelectedEntity,
    isClientContext,
    resetToRPS,
    NPANavigationClientContext,
  } = useSimulatorStore()
  const { mutate: resumeMutate, isLoading: resumeIsLoading } =
    useResumePetisSimulationProposal()
  const { mutate: loadPolicyHolderReferenceDataMutate } =
    useLoadPolicyHolderReferenceData()
  const {
    mutate: dataForSimulationMutate,
    data: dataForSimulation,
    isLoading: dataForSimulationIsLoading,
    isError,
  } = useFillDataForSimulation()

  const conditionalShowSelectedEntity = useMemo(() => {
    return (
      (!isGenerateProposalDocuments &&
        isClientContext &&
        (NPANavigationClientContext?.data?.clientEntityID || '')?.length ===
          0) ||
      (!isClientContext &&
        (getEntitiesDetails?.matchedEntities || [])?.length > 1)
    )
  }, [
    NPANavigationClientContext?.data?.clientEntityID,
    getEntitiesDetails?.matchedEntities,
    isClientContext,
    isGenerateProposalDocuments,
  ])

  const handleValidateDate = (value: string) => {
    const _initialDate = isResume ? value : TODAY

    if ((value || '').length === 0) {
      setMessageDate(t('pages.step1.errors.mandatoryField'))
      setValidateInitialDate(false)
      return
    }

    const valueDate = moment(value, 'DD-MM-YYYY')
    const dateToValidate = moment(THIRTY_DAYS_FROM_TODAY, 'DD-MM-YYYY')
    const dateToValidatePast = moment(_initialDate, 'DD-MM-YYYY')

    const isValid = valueDate.isBetween(
      dateToValidatePast,
      dateToValidate,
      null,
      '[]',
    )

    setMessageDate('')
    setValidateInitialDate(isValid)

    return isValid
  }

  const handleResetReferrals = () => {
    if (
      enabledRunPetisRequest &&
      (runPetisSimulation?.runPetisSimulationResponse?.quoteId || '')?.length >
        0
    ) {
      setIsReferral(false)
      resetToRPS()
      animalsList.forEach((animal) => {
        updateAnimal(
          {
            pack: {
              name: '',
              price: '',
              installment: '',
            },
          },
          animal.id,
        )
      })
    }
  }

  const handleNextStepAction = () => {
    const allDataIsValid = Object.values(isValid || { initial: false }).every(
      Boolean,
    )

    if (isFiscalNumberFilled && !isFiscalNumberSearched) {
      setWarningOptions({
        labelTypes: [t('pages.step2.report.search')],
        mode: WARNING_BAR.mode.show,
      })

      return
    }

    if (allDataIsValid) {
      setIsReferral(false)
      setIsFullStop(false)
      setSelectedPackCampaign(null)
      setIsRecalculate(false)
      resetTotalPricesObjects()
    }

    if (requestQuotation !== null) {
      resetRequestQuotation()
      resetGetQuotation()
    }

    if (allDataIsValid && animalsList.length > 0) {
      if (animalsList.at(-1)?.name?.length === 0) {
        updateAnimal(
          { name: `Animal${Number(animalsList.at(-1)?.id || 0)}` },
          animalsList.at(-1)?.id || '0',
        )
      }

      setHasRunPetisUpdated(true)
      handleResetReferrals()
      navigate(ROUTES.simulationResult)
    } else {
      setWarningOptions({
        labelTypes: [t('pages.step1.errors.validateData')],
        mode:
          allDataIsValid && animalsList.length > 0
            ? WARNING_BAR.mode.hide
            : WARNING_BAR.mode.show,
      })
    }

    handleUpdateURLWithoutReload()
  }

  useEffect(() => {
    if (agentIdBySessionStorage && globalContext?.username?.length > 0) {
      if (LocalStorageService.get(LOCAL_STORAGE.animalStore) !== null) {
        handleNewSimulation()
        setIsStorageClear(true)
      } else if (fillDataForSimulationRequest === null) {
        loadPolicyHolderReferenceDataMutate()
        dataForSimulationMutate(agentIdBySessionStorage || '')
      }
    }
  }, [
    agentIdBySessionStorage,
    dataForSimulationMutate,
    globalContext,
    loadPolicyHolderReferenceDataMutate,
  ])

  useEffect(() => {
    if (isStorageClear) {
      handleClearLocalStorage()
    }
  }, [isStorageClear])

  useEffect(() => {
    setRequestId(referenceNumber || '')
  }, [location.search])

  useEffect(() => {
    if (requestId && !isResume) {
      resumeMutate(resumePetisSimulationProposalPayload)
    }
  }, [requestId, isResume])

  useLayoutEffect(() => {
    setCurrentStep(1)
  }, [setCurrentStep])

  useEffect(() => {
    setIsResumeLoading(resumeIsLoading)
  }, [resumeIsLoading])

  useEffect(() => {
    handleValidateDate(initialDate)
  }, [initialDate])

  return (
    <MainContainer>
      <SimulateDataContainer>
        <PolicyData
          id="policy-detail"
          version="2"
          colorTheme="blue"
          name={t('pages.step1.policyData')}
          open={true}
          detail={true}
          components={
            <>
              <PolicyDataElements key="policyDataElements">
                <Date
                  Picker
                  id="initialDateId"
                  key={`date-${initialDate}-${t('pages.step1.initialDate')}`}
                  label={t('pages.step1.initialDate')}
                  pastDate={isResume ? initialDate : TODAY}
                  futureDate={THIRTY_DAYS_FROM_TODAY}
                  format="d-m-Y"
                  value={initialDate}
                  message={messageDate}
                  colorTheme="blue"
                  onValueChanged={(key: any, value: any) => {
                    if (initialDate !== value) {
                      setEnabledRunPetisRequest(true)
                    }

                    handleValidateDate(value)
                    setInitialDate(value)
                  }}
                  version="2"
                  readOnly={
                    isGenerateProposalDocuments ||
                    disabledStepsByResume.step1 ||
                    hasCreatedProposal
                  }
                />
                <Entity
                  initialDate={initialDate}
                  disabled={disabledStepsByResume.step1 || hasCreatedProposal}
                  setLoading={setIsEntityDetailsLoading}
                  setIsFiscalNumberFilled={setIsFiscalNumberFilled}
                  setIsFiscalNumberSearched={setIsFiscalNumberSearched}
                />
              </PolicyDataElements>
              {(isEntityDetailsLoading || resumeIsLoading) && (
                <MiniLoader
                  color="transparent"
                  minHeight="auto"
                  maxWidth="auto"
                />
              )}
              {!isEntityDetailsLoading &&
                (
                  entitySelected?.vatNumber ||
                  policyholderDetailsSelected?.vatNumber ||
                  ''
                ).length > 0 && (
                  <>
                    <UserInfo
                      key="userInfo"
                      name={
                        (entitySelected?.name as string) ||
                        (policyholderDetailsSelected?.name as string)
                      }
                      birthdate={
                        (entitySelected?.birthdate as string) ||
                        (policyholderDetailsSelected?.birthdate as string)
                      }
                    />
                    {conditionalShowSelectedEntity && (
                      <div className="button-create">
                        <Link
                          label={t('pages.step1.selectEntity')}
                          onValueChanged={() => setShowSelectedEntity(true)}
                        />
                      </div>
                    )}
                  </>
                )}
            </>
          }
        />
        <AnimalInsuranceTable
          isValid={isValid as never}
          setIsValid={setIsValid}
          disabled={disabledStepsByResume.step1 || hasCreatedProposal}
          species={dataForSimulation?.petDetailsResponse?.petSpeciesList}
          isLoading={dataForSimulationIsLoading || resumeIsLoading}
          isError={isError}
        />
        {dataForSimulationIsLoading && (
          <MiniLoader color="transparent" minHeight="auto" maxWidth="auto" />
        )}
        {fillDataForSimulationRequest?.informativeNotesResponse?.title && (
          <LegalInformation
            data={
              fillDataForSimulationRequest?.informativeNotesResponse as never
            }
          />
        )}
        <div className="button-action">
          <Action
            id="action1"
            onValueChanged={handleNextStepAction}
            colorTheme="blue"
            buttonType="primary"
            label={t('pages.step1.simulate')}
            disable={
              !validateInitialDate ||
              isEntityDetailsLoading ||
              isGenerateProposalDocuments ||
              !Object.values(isValid || { initial: false }).every(Boolean) ||
              (isFiscalNumberFilled && !isFiscalNumberSearched) ||
              disabledStepsByResume.step1 ||
              hasCreatedProposal
            }
          />
        </div>
      </SimulateDataContainer>
    </MainContainer>
  )
}
